import { useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { Card, FormControl, LoadingButton, PageTitle, PageWrapper } from '@ingrid/luterkort-react';

import { FormLeaveBlocker } from '~src/components/FormLeaveBlocker/FormLeaveBlocker';
import { MutationBanner } from '~src/components/MutationNotification/MutationNotification';
import { MutationSaveBar } from '~src/components/MutationSaveBar/MutationSaveBar';
import { useTranslation } from '~src/i18n/use-translation';
import { useToast } from '~src/main/hooks/useToast';
import { hasTouchedFields } from '~src/utils/form';

import { Breadcrumb } from '../breadcrumb';
import { AuthFormModal } from '../create/auth-form-modal';
import { BasicInfo } from '../create/basic-info';
import { Credentials } from '../create/credentials';
import { Menu } from '../menu';
import { useCarrierContractUpdateMutation } from '../mutations';
import { useCarrierContractSuspenseQuery } from '../queries';
import { CarrierContractUpsert, useCarrierContractForm } from '../types';
import { Carrier } from './carrier';

export function Page() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthFormOpen, setIsAuthFormOpen] = useState(false);

  const { id } = useParams();
  const { data } = useCarrierContractSuspenseQuery({ contract_id: id });

  const form = useCarrierContractForm({
    defaultValues: {
      carrier_configuration_data: data.carrier_configuration_data,
      description: data.description ?? '',
      schema_id: data.schema_id,
      name: data.name,
    },
  });

  const mutation = useCarrierContractUpdateMutation({ contract_id: id });
  const disableClear = !form.formState.isDirty || !hasTouchedFields(form.formState.touchedFields);
  const schemaId = form.watch('schema_id');
  const viewportRef = useRef(null);
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { toast } = useToast();

  async function onSubmit(data: CarrierContractUpsert) {
    await mutation.mutateAsync({
      update_mask: Object.keys(form.formState.dirtyFields),
      ...data,
    });

    toast({
      title: t('carrierContracts.details.updateSuccess'),
      variant: 'success',
    });

    navigate(`../${id}`);
  }

  function handleAuthComplete(data: object) {
    setIsAuthenticated(true);

    form.setValue('carrier_configuration_data', {
      ...form.getValues('carrier_configuration_data'),
      ...data,
    });
  }

  function handleClear() {
    form.reset(data, { keepDefaultValues: true, keepValues: false, keepTouched: false });
  }

  return (
    <PageWrapper variant="center" style={{ marginBottom: MutationSaveBar.Offset }}>
      <FormProvider {...form}>
        <FormControl.Root>
          <FormControl.Form onSubmit={form.handleSubmit(onSubmit)} style={{ display: 'none' }} />

          <PageTitle.Wrapper ref={viewportRef}>
            <PageTitle.Stack>
              <Breadcrumb currentPage={t('common.edit')} />
            </PageTitle.Stack>

            <FormControl.SubmitButton asChild>
              <LoadingButton loading={mutation.isPending}>{t('common.save')}</LoadingButton>
            </FormControl.SubmitButton>

            <Menu disabled={disableClear}>
              <Menu.ClearItem onClick={handleClear} disabled={disableClear} />
              <Menu.DuplicateItem contractId={data.contract_id!} />
              <Menu.DeleteItem contractId={data.contract_id!} onSuccess={() => navigate('..')} />
            </Menu>
          </PageTitle.Wrapper>

          <MutationBanner mutation={mutation} />

          <Card.List>
            <BasicInfo />
            <Carrier contract={data} />
            <Credentials onShowAuthForm={() => setIsAuthFormOpen(true)} isAuthFormDisabled={isAuthenticated} />
          </Card.List>

          <FormLeaveBlocker formState={form.formState} />

          <MutationSaveBar viewportRef={viewportRef}>
            <MutationSaveBar.Cancel to=".." />

            <MutationSaveBar.Submit
              isLoading={mutation.isPending}
              onClick={form.handleSubmit(onSubmit)}
              label={t('common.save')}
            />
          </MutationSaveBar>
        </FormControl.Root>
      </FormProvider>

      {schemaId && (
        <AuthFormModal
          onAuthComplete={handleAuthComplete}
          setIsOpen={setIsAuthFormOpen}
          isOpen={isAuthFormOpen}
          schemaId={schemaId}
        />
      )}
    </PageWrapper>
  );
}
